import React from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { CreditCardIcon, CarIcon, HomeIcon, MedicalIcon, TaxIcon, BankIcon } from '../ui/Icons';

type DebtCategoryKey = 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';

interface DebtCategory {
  key: DebtCategoryKey;
  label: string;
  Icon: React.FC<{ className?: string }>;
}

interface DebtItem {
  id: string;
  category: DebtCategoryKey;
  creditor: string;
  amount: number;
  accountNumber?: string;
  dueDate?: string;
  notes?: string;
  documents?: Array<{
    name: string;
    url: string;
    uploadDate: string;
  }>;
}

const Frame236: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();

  const debts: DebtItem[] = session.taskProgress?.['debts']?.answers?.debts || [];

  const categories: DebtCategory[] = [
    { key: 'credit_card', label: 'Credit Card Debt', Icon: CreditCardIcon },
    { key: 'personal_auto', label: 'Personal & Auto Loans', Icon: CarIcon },
    { key: 'mortgage', label: 'Property Mortgages', Icon: HomeIcon },
    { key: 'medical', label: 'Medical Expenses', Icon: MedicalIcon },
    { key: 'tax', label: 'Tax Debt', Icon: TaxIcon },
    { key: 'other', label: 'Other Loans', Icon: BankIcon },
  ];

  const handleEdit = () => navigate('/frame-235');
  const handleBack = () => navigate('/frame-235');

  // Read-only "Complete": move forward to the real summary frame (where FinishFlowAction should be)
  const handleComplete = () => {
    navigate('/frame-237'); // change to '/frame-237' if that’s your intended next step
  };

  const totalDebt = debts.reduce((sum, debt) => sum + (Number(debt.amount) || 0), 0);

  const formatMoney = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const formatDate = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  const maskAccount = (acct?: string) => {
    if (!acct) return null;
    const last4 = acct.slice(-4);
    return last4 ? `****${last4}` : null;
  };

  return (
    <ResponsiveContainer background="stone">
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2">
        <BrandSymbol size="3xl" showText variant="dark" />
      </div>

      <ResponsiveContent className="pt-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-cyan-900 text-3xl font-semibold font-['Test_Domaine_Display'] mb-8">
            Debts and Liabilities Summary
          </h1>

          <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-8">
            <div className="text-2xl font-semibold text-stone-800 mb-2">Total Outstanding Debt</div>
            <div className="text-4xl font-bold text-cyan-700">{formatMoney(totalDebt)}</div>
          </div>

          {debts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-stone-600 text-lg">No debts or liabilities have been added.</p>
              <button
                onClick={handleEdit}
                className="mt-4 px-6 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800"
              >
                Add Debts
              </button>
            </div>
          ) : (
            <>
              {categories.map((category) => {
                const categoryDebts = debts.filter((d) => d.category === category.key);
                if (categoryDebts.length === 0) return null;

                const categoryTotal = categoryDebts.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

                return (
                  <div key={category.key} className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <category.Icon className="w-6 h-6 text-cyan-700" />
                      <h2 className="text-xl font-semibold text-stone-800">{category.label}</h2>
                      <div className="text-lg font-medium text-cyan-700">{formatMoney(categoryTotal)}</div>
                    </div>

                    <div className="space-y-4">
                      {categoryDebts.map((debt) => {
                        const acctMasked = maskAccount(debt.accountNumber);
                        const due = formatDate(debt.dueDate);

                        return (
                          <div key={debt.id} className="bg-stone-50 rounded-lg p-4 border border-stone-100">
                            <div className="font-medium text-stone-800">{debt.creditor || 'Unknown creditor'}</div>

                            <div className="text-sm text-stone-500 mt-1">
                              Amount: {formatMoney(Number(debt.amount) || 0)}
                              {acctMasked ? ` • Account: ${acctMasked}` : ''}
                              {due ? ` • Due: ${due}` : ''}
                            </div>

                            {debt.notes ? (
                              <div className="text-sm text-stone-600 mt-2">{debt.notes}</div>
                            ) : null}

                            {debt.documents?.length ? (
                              <div className="text-sm text-cyan-600 mt-2">
                                {debt.documents.length} document(s) attached
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-center mt-8">
                <button onClick={handleEdit} className="px-6 py-2 text-cyan-700 hover:text-cyan-800 font-medium">
                  Edit Debts and Liabilities
                </button>
              </div>
            </>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Track Debts and Final Expenses"
        subtitle="Review Summary"
        progressPercent={66}
        onNext={handleComplete}
        onBack={handleBack}
        nextText="Complete →"
        backText="← Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame236;